import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "./use-debounce";
import { useBrandOnboarding } from "@/context/BrandOnboardingContextFixed";

interface NavigationRule {
  condition: boolean;
  path: string;
  delay?: number;
}

export function useDebouncedNavigation(
  status: string | undefined,
  isLoading: boolean,
  rules: NavigationRule[]
) {
  const navigate = useNavigate();
  const lastNavigationRef = useRef<string>("");
  const navigationTimeoutRef = useRef<NodeJS.Timeout>();
  const { isCompletingOnboarding } = useBrandOnboarding();
  
  // Debounce the status to prevent rapid navigation triggers
  const debouncedStatus = useDebounce(status, 300);
  
  useEffect(() => {
    // Clear any pending navigation
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    // Don't navigate while loading, if no status, or if completing onboarding
    if (isLoading || !debouncedStatus || isCompletingOnboarding) {
      if (isCompletingOnboarding) {
        console.log("⏸️ Debounced navigation: Skipping navigation - onboarding completion in progress");
      }
      return;
    }
    
    const currentPath = window.location.pathname;
    
    // Find the first matching rule
    const matchingRule = rules.find(rule => rule.condition);
    
    if (matchingRule && matchingRule.path !== currentPath) {
      // Prevent duplicate navigation to the same path
      if (lastNavigationRef.current === matchingRule.path) {
        console.log("🚫 Preventing duplicate navigation to:", matchingRule.path);
        return;
      }
      
      console.log("🧭 Debounced navigation triggered:", {
        from: currentPath,
        to: matchingRule.path,
        status: debouncedStatus,
        delay: matchingRule.delay || 500
      });
      
      navigationTimeoutRef.current = setTimeout(() => {
        lastNavigationRef.current = matchingRule.path;
        navigate(matchingRule.path, { replace: true });
      }, matchingRule.delay || 500);
    }
    
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [debouncedStatus, isLoading, navigate, rules]);
  
  // Reset navigation tracking when status changes significantly
  useEffect(() => {
    lastNavigationRef.current = "";
  }, [status]);
}